import { ref, nextTick } from 'vue';

export type PdfRendererOptions = {
  onPageRendered?: () => void;
  onError?: (error: string) => void;
};

export const usePdfRenderer = (options: PdfRendererOptions = {}) => {
  const isLoading = ref(true);
  const error = ref<string | null>(null);
  const pdfCanvas = ref<HTMLCanvasElement | null>(null);
  const pdfDocument = ref<any>(null);
  const currentPage = ref(1);
  const totalPages = ref(0);

  const originalPageWidth = ref<number>(0);
  const originalPageHeight = ref<number>(0);
  const canvasWidth = ref(400);
  const canvasHeight = ref(300);
  const pdfScale = ref(1);

  const loadPDFJS = async () => {
    // Polyfill URL.parse for older browsers (Chrome < 126)
    if (!URL.parse) {
      URL.parse = (url: string, base?: string | URL) => {
        try {
          return new URL(url, base);
        } catch {
          return null;
        }
      };
    }

    const pdfjsLib = await import('pdfjs-dist');

    if (!pdfjsLib.GlobalWorkerOptions.workerSrc) {
      pdfjsLib.GlobalWorkerOptions.workerSrc = new URL(
        'pdfjs-dist/build/pdf.worker.min.mjs',
        import.meta.url,
      ).toString();
    }

    return pdfjsLib;
  };

  const renderCurrentPage = async () => {
    if (!pdfDocument.value || !pdfCanvas.value) {
      return;
    }

    try {
      let page;
      try {
        page = await pdfDocument.value.getPage(currentPage.value);
      } catch (err: unknown) {
        try {
          const pdfjsLib = await loadPDFJS();
          const loadingTask = pdfjsLib.getDocument({
            url: pdfCanvas.value.dataset.pdfUrl || '',
            disableAutoFetch: true,
            disableStream: true,
          });
          const freshPdf = await loadingTask.promise;
          page = await freshPdf.getPage(currentPage.value);
          pdfDocument.value = freshPdf;
        } catch {
          const errorMessage =
            err instanceof Error ? err.message : 'Unknown error';
          throw new Error(
            `Cannot access page ${currentPage.value}: ${errorMessage}`,
          );
        }
      }

      const canvas = pdfCanvas.value;
      const context = canvas.getContext('2d');

      if (!context) {
        throw new Error('Could not get canvas context');
      }

      const container = canvas.parentElement;
      const containerWidth = container?.clientWidth || 0;
      const viewportWidth =
        (globalThis.window !== undefined && window.innerWidth) ||
        (typeof document !== 'undefined' &&
          document.documentElement.clientWidth) ||
        600;
      const baseWidth =
        containerWidth > 0 ? containerWidth : Math.max(viewportWidth - 32, 320);
      const effectiveWidth = Math.max(baseWidth - 40, 200);
      const maxWidth = Math.min(effectiveWidth, 800);

      const viewport = page.getViewport({ scale: 1 });

      originalPageWidth.value = viewport.width;
      originalPageHeight.value = viewport.height;

      const targetScale = maxWidth / viewport.width;
      const scaledViewport = page.getViewport({ scale: targetScale });

      canvas.width = scaledViewport.width;
      canvas.height = scaledViewport.height;
      canvas.style.width = `${scaledViewport.width}px`;
      canvas.style.height = `${scaledViewport.height}px`;

      pdfScale.value = targetScale;
      canvasWidth.value = scaledViewport.width;
      canvasHeight.value = scaledViewport.height;

      context.clearRect(0, 0, canvas.width, canvas.height);

      const renderContext = {
        canvasContext: context,
        viewport: scaledViewport,
        enableWebGL: false,
      };

      try {
        const renderTask = page.render(renderContext);
        await renderTask.promise;
      } catch {
        const simpleRenderContext = {
          canvasContext: context,
          viewport: scaledViewport,
        };
        const fallbackRenderTask = page.render(simpleRenderContext);
        await fallbackRenderTask.promise;
      }

      await nextTick();
      options.onPageRendered?.();
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error';
      const errorText = `Failed to render page ${currentPage.value}: ${errorMessage}`;
      error.value = errorText;
      options.onError?.(errorText);
    }
  };

  const loadPDF = async (fileUrl: string) => {
    if (!fileUrl || !pdfCanvas.value) return;

    try {
      isLoading.value = true;
      error.value = null;

      pdfCanvas.value.dataset.pdfUrl = fileUrl;

      const pdfjsLib = await loadPDFJS();

      const loadingTask = pdfjsLib.getDocument({
        url: fileUrl,
        disableAutoFetch: true,
        disableStream: true,
        disableRange: true,
      });

      const pdf = await loadingTask.promise;
      pdfDocument.value = pdf;
      totalPages.value = pdf.numPages;
      currentPage.value = 1;

      await renderCurrentPage();
    } catch {
      const errorText = 'Failed to load PDF. Please try again.';
      error.value = errorText;
      options.onError?.(errorText);
    } finally {
      isLoading.value = false;
    }
  };

  const nextPage = async () => {
    if (currentPage.value < totalPages.value) {
      currentPage.value++;
      await renderCurrentPage();
    }
  };

  const previousPage = async () => {
    if (currentPage.value > 1) {
      currentPage.value--;
      await renderCurrentPage();
    }
  };

  const getCanvasDimensions = () => {
    return {
      width: canvasWidth.value,
      height: canvasHeight.value,
      originalWidth: originalPageWidth.value,
      originalHeight: originalPageHeight.value,
    };
  };

  return {
    isLoading,
    error,
    pdfCanvas,
    currentPage,
    totalPages,
    canvasWidth,
    canvasHeight,
    originalPageWidth,
    originalPageHeight,
    pdfScale,

    loadPDF,
    renderCurrentPage,
    nextPage,
    previousPage,
    getCanvasDimensions,
  };
};
